package com.speedsoftware.nibbler.ui.boards

import androidx.compose.ui.draw.clip
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.models.BoardColumnDto
import com.speedsoftware.nibbler.ui.theme.NibblerColors

/** Accent colors a column can use (hex strings are what the server stores). */
val ColumnColorPalette = listOf(
    "#7AA7F7", // blue
    "#F5B544", // amber
    "#8B7CF6", // violet
    "#34D399", // green
    "#F87171", // red
    "#E879F9", // pink
    "#9CA3AF"  // grey
)

fun parseColumnColor(hex: String): Color {
    val cleaned = hex.removePrefix("#")
    val value = cleaned.toLongOrNull(16) ?: return NibblerColors.ElectricIndigo
    return when (cleaned.length) {
        6 -> Color((0xFF000000L or value).toInt())
        8 -> Color(value.toInt())
        else -> NibblerColors.ElectricIndigo
    }
}

/** Three-dot "more" icon, drawn on a canvas because the web canvas has no glyph fallback fonts. */
@Composable
private fun KebabIcon(color: Color, modifier: Modifier = Modifier) {
    Canvas(modifier = modifier.size(width = 4.dp, height = 16.dp)) {
        val r = 1.5.dp.toPx()
        for (i in 0..2) {
            drawCircle(color = color, radius = r, center = Offset(size.width / 2f, size.height * (0.16f + 0.34f * i)))
        }
    }
}

/** Header menu of a board column: rename/recolor, reorder and delete. */
@Composable
fun ColumnMenuButton(
    canMoveLeft: Boolean,
    canMoveRight: Boolean,
    canDelete: Boolean,
    onEdit: () -> Unit,
    onMoveLeft: () -> Unit,
    onMoveRight: () -> Unit,
    onDelete: () -> Unit,
    modifier: Modifier = Modifier
) {
    var expanded by remember { mutableStateOf(false) }
    Box(modifier = modifier) {
        Box(
            modifier = Modifier
                .clickable { expanded = true }
                .padding(horizontal = 8.dp, vertical = 4.dp)
        ) {
            KebabIcon(color = NibblerColors.DarkOnSurfaceVariant)
        }
        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            modifier = Modifier.background(NibblerColors.SurfaceContainerHigh)
        ) {
            DropdownMenuItem(
                text = { Text("Rename / color", color = NibblerColors.OnSurface, fontSize = 13.sp) },
                onClick = { expanded = false; onEdit() }
            )
            if (canMoveLeft) {
                DropdownMenuItem(
                    text = { Text("Move left", color = NibblerColors.OnSurface, fontSize = 13.sp) },
                    onClick = { expanded = false; onMoveLeft() }
                )
            }
            if (canMoveRight) {
                DropdownMenuItem(
                    text = { Text("Move right", color = NibblerColors.OnSurface, fontSize = 13.sp) },
                    onClick = { expanded = false; onMoveRight() }
                )
            }
            if (canDelete) {
                DropdownMenuItem(
                    text = { Text("Delete status", color = NibblerColors.Error, fontSize = 13.sp) },
                    onClick = { expanded = false; onDelete() }
                )
            }
        }
    }
}

/** Trailing placeholder at the end of the board that opens the "add status" dialog. */
@Composable
fun AddColumnPlaceholder(onClick: () -> Unit, modifier: Modifier = Modifier) {
    Box(
        modifier = modifier
            .width(240.dp)
            .fillMaxHeight()
            .clip(RoundedCornerShape(8.dp))
            .clickable { onClick() }
            .background(NibblerColors.SurfaceContainerLow)
            .border(1.dp, NibblerColors.OutlineVariant, RoundedCornerShape(8.dp)),
        contentAlignment = Alignment.TopCenter
    ) {
        Text(
            text = "+ Add status",
            modifier = Modifier.padding(top = 20.dp),
            fontWeight = FontWeight.Bold,
            fontSize = 13.sp,
            color = NibblerColors.OnSurfaceVariant
        )
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun ColorSwatchRow(selected: String, onSelect: (String) -> Unit) {
    FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
        ColumnColorPalette.forEach { hex ->
            val isSelected = hex.equals(selected, ignoreCase = true)
            Box(
                modifier = Modifier
                    .size(28.dp)
                    .background(parseColumnColor(hex), RoundedCornerShape(8.dp))
                    .border(
                        width = if (isSelected) 2.dp else 1.dp,
                        color = if (isSelected) NibblerColors.OnSurface else NibblerColors.OutlineVariant,
                        shape = RoundedCornerShape(8.dp)
                    )
                    .clickable { onSelect(hex) }
            )
        }
    }
}

/** Dialog used both to add a new status and to rename / recolor an existing one. */
@Composable
fun ColumnEditDialog(
    title: String,
    confirmLabel: String,
    initialName: String,
    initialColor: String,
    onDismiss: () -> Unit,
    onConfirm: (name: String, color: String) -> Unit
) {
    var name by remember { mutableStateOf(initialName) }
    var color by remember { mutableStateOf(initialColor) }

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = NibblerColors.SurfaceContainerHigh,
        shape = RoundedCornerShape(8.dp),
        title = { Text(title, color = NibblerColors.OnSurface) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                OutlinedTextField(
                    value = name,
                    onValueChange = { if (it.length <= 32) name = it },
                    label = { Text("Status name") },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth()
                )
                Text("Color:", style = MaterialTheme.typography.labelMedium, color = NibblerColors.OnSurface)
                ColorSwatchRow(selected = color, onSelect = { color = it })
            }
        },
        confirmButton = {
            Button(
                onClick = { if (name.isNotBlank()) onConfirm(name.trim(), color) },
                enabled = name.isNotBlank(),
                shape = RoundedCornerShape(8.dp),
                colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Primary)
            ) { Text(confirmLabel) }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text("Cancel", color = NibblerColors.OnSurfaceVariant) }
        }
    )
}

/** Confirmation for deleting a status; the tasks it contains are moved to a chosen remaining status. */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun DeleteColumnDialog(
    column: BoardColumnDto,
    otherColumns: List<BoardColumnDto>,
    taskCount: Int,
    onDismiss: () -> Unit,
    onConfirm: (moveToColumnId: String) -> Unit
) {
    var moveTo by remember { mutableStateOf(otherColumns.firstOrNull()?.id ?: "") }

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor = NibblerColors.SurfaceContainerHigh,
        shape = RoundedCornerShape(8.dp),
        title = { Text("Delete status \"${column.name}\"?", color = NibblerColors.OnSurface) },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                if (taskCount > 0) {
                    Text(
                        "$taskCount issue(s) in this status will be moved to:",
                        style = MaterialTheme.typography.bodyMedium,
                        color = NibblerColors.OnSurfaceVariant
                    )
                    FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                        otherColumns.forEach { target ->
                            val isSelected = target.id == moveTo
                            Box(
                                modifier = Modifier
                                    .clip(RoundedCornerShape(8.dp))
                                    .clickable { moveTo = target.id }
                                    .background(
                                        if (isSelected) NibblerColors.PrimaryContainer else NibblerColors.SurfaceContainer,
                                        RoundedCornerShape(8.dp)
                                    )
                                    .border(
                                        1.dp,
                                        if (isSelected) NibblerColors.PrimaryContainer else NibblerColors.OutlineVariant,
                                        RoundedCornerShape(8.dp)
                                    )
                                    .padding(horizontal = 10.dp, vertical = 6.dp)
                            ) {
                                Text(
                                    target.name,
                                    fontSize = 12.sp,
                                    color = if (isSelected) Color.White else NibblerColors.OnSurface
                                )
                            }
                        }
                    }
                } else {
                    Text("This status has no issues.", style = MaterialTheme.typography.bodyMedium, color = NibblerColors.OnSurfaceVariant)
                }
            }
        },
        confirmButton = {
            Button(
                onClick = { onConfirm(moveTo) },
                shape = RoundedCornerShape(8.dp),
                colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.ErrorContainer, contentColor = NibblerColors.Error)
            ) { Text("Delete") }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text("Cancel", color = NibblerColors.OnSurfaceVariant) }
        }
    )
}

@androidx.compose.ui.tooling.preview.Preview
@Composable
private fun AddColumnPlaceholderPreview() {
    Column(modifier = Modifier.background(NibblerColors.Background).padding(16.dp).height(160.dp)) {
        AddColumnPlaceholder(onClick = {})
    }
}

@androidx.compose.ui.tooling.preview.Preview
@Composable
private fun ColumnEditDialogPreview() {
    ColumnEditDialog(
        title = "Add status",
        confirmLabel = "Add",
        initialName = "",
        initialColor = ColumnColorPalette.first(),
        onDismiss = {},
        onConfirm = { _, _ -> }
    )
}

@androidx.compose.ui.tooling.preview.Preview
@Composable
private fun ColumnMenuButtonPreview() {
    Row(
        modifier = Modifier.background(NibblerColors.Background).padding(16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Spacer(modifier = Modifier.width(8.dp))
        ColumnMenuButton(
            canMoveLeft = true,
            canMoveRight = true,
            canDelete = true,
            onEdit = {}, onMoveLeft = {}, onMoveRight = {}, onDelete = {}
        )
    }
}
